/**
 * =============================================================================
 * setera-web
 * =============================================================================
 * Copyright (c) 2025 HG Digital/Setera
 * Todos os direitos reservados.
 * 
 * Este software é confidencial e de propriedade da HG Digital/Setera.
 * Seu uso está sujeito aos termos do contrato de licença.
 * -----
 * Criado em : 15-05-2025 16:50:21
 * Autor     : Diego Fabiano Brock
 * Projeto   : setera-web
 * Versão    : 1.0.0
 * =============================================================================
 */

import { toCamel, toSnake } from 'snake-camel';
import { Component, Input, OnInit, Inject } from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { MatTableDataSource } from '@angular/material/table';
import { CommandService } from '../../../services/command.service';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { FormBuilder, FormGroup, FormArray, Validators, FormControl, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MessageFieldModel, SelectOption, SelectOptions } from '../../../models/message-field-model';
import { CommandModel } from '../../../models/command-model';
import { CommandModelPaginated } from '../../../models/command-model-paginated';
import { CommandExecutionRequest } from '../../../models/command-execution-request.model';
import { AuthenticationService } from '../../../services/authentication.service';
import { Observable, throwError } from 'rxjs';
import { switchMap, catchError, tap } from 'rxjs/operators';
import { CommandAuthDialogComponent } from './command-auth-dialog.component';
import { CommandHistoryConfirmDialogComponent } from './command-history-confirm-dialog.component';
import { isInsufficientAuthError } from '../../../services/step-up-errors';
import { CommandActionAccessService } from '../../../services/command-action-access.service';
import {
  CommandExecutionHistoryItem,
  commandExecutionStatusLabel,
  executionRequiresReauth,
  extractExecutionPageInfo,
  hasCancelLink,
  hasResendLink,
} from '../../../models/command-execution-history.model';

/** Layouts do dialog: atalhos, um veículo ou lote (Selecionar/proximidade). */
export type CommandSenderLayout = 'classic' | 'generic' | 'batch';

/**
 * Interface para configuração de customização do componente
 * Permite personalizar o título do dialog e o label do botão de envio
 */
export interface CommandSenderConfig {
  /** Título personalizado do dialog (padrão: 'Parâmetros do comando') */
  dialogTitle?: string;
  /** Label personalizado do botão de envio (padrão: 'Enviar comando') */
  submitButtonLabel?: string;
  /**
   * classic: atalhos (imagens/vídeo).
   * generic: opção Comandos de um veículo.
   * batch: múltiplos terminais com seleção (toolbox Selecionar).
   */
  layout?: CommandSenderLayout;
}

/**
 * Componente para envio de comandos aos terminais
 * 
 * @description
 * Este componente permite enviar comandos aos terminais de forma dinâmica,
 * construindo formulários baseados nos metadados retornados pela API.
 * 
 * @example
 * // Uso básico com valores padrão
 * <app-command-sender
 *   [terminalId]="'123'"
 *   commandAction="FILE_UPLOAD"
 *   mode="NORMAL">
 * </app-command-sender>
 * 
 * @example
 * // Uso com configurações customizadas
 * <app-command-sender
 *   [terminalId]="'123'"
 *   commandAction="LIVE_VIDEO_REQUEST"
 *   mode="NORMAL"
 *   [config]="{ 
 *     dialogTitle: 'Configurar Streaming', 
 *     submitButtonLabel: 'Iniciar Streaming' 
 *   }">
 * </app-command-sender>
 * 
 * @example
 * // Uso via MatDialog com configurações customizadas
 * const dialogRef = this.dialog.open(CommandSenderComponent, {
 *   width: '600px',
 *   data: {
 *     terminalId: '123',
 *     commandAction: 'LIVE_VIDEO_CONTROL',
 *     mode: 'DIRECT',
 *     config: {
 *       dialogTitle: 'Finalizar Streaming',
 *       submitButtonLabel: 'Finalizar'
 *     }
 *   }
 * });
 */
@Component({
  selector: 'app-command-sender',
  templateUrl: './command-sender.component.html',
  styleUrls: ['./command-sender.component.scss']
})
export class CommandSenderComponent implements OnInit {
  /** ID do terminal para envio do comando */
  @Input() terminalId: string;
  /** Ação do comando a ser executado */
  @Input() commandAction?: string;
  /** Modo de operação: 'NORMAL' (com interface) ou 'DIRECT' (execução direta) */
  @Input() mode?: string;
  /** Configurações customizadas para personalização da interface */
  @Input() config?: CommandSenderConfig;

  // Propriedades customizáveis com valores padrão
  dialogTitle: string = 'Parâmetros do comando';
  submitButtonLabel: string = 'Enviar comando';
  layout: CommandSenderLayout = 'classic';

  availableCommands: CommandModelPaginated = new CommandModelPaginated();
  /** Null até o usuário escolher (ou até restar um único comando). */
  selectedCommand: CommandModel | null = null;
  dialogRef: MatDialogRef<CommandSenderComponent>;
  prepareForm: CommandModel = new CommandModel();
  loading = false;
  errorMessage: string = '';
  successMessage: string = '';
  historyLoading = false;
  historyErrorMessage: string = '';
  historySuccessMessage: string = '';
  sentCommandItems: CommandExecutionHistoryItem[] = [];
  historyActionUuid: string | null = null;
  historyPageIndex = 0;
  historyPageSize = 10;
  historyTotalElements = 0;
  /** Resposta bruta da listagem (links HAL first/next/prev/last). */
  currentHistoryPageResponse: any = null;

  /** Helpers expostos ao template (paridade admin-web). */
  hasResendLink = hasResendLink;
  hasCancelLink = hasCancelLink;
  commandExecutionStatusLabel = commandExecutionStatusLabel;

  form: FormGroup;
  messageFields: MessageFieldModel[] = [];
  fieldOptions: Map<string, SelectOption[]> = new Map();
  /** Delimitador para juntar valores de campos multi-valor (prepare-form). */
  charDelimiter = ',';

  streamingSessionUuid?: string;

  /** Terminais exibidos no modo lote (toolbox Selecionar/proximidade). */
  terminals: any[] = [];
  /** Pré-seleção recebida do mapa. */
  preselectedTerminals: any[] = [];
  displayedTerminalColumns: string[] = ['select', 'plate'];
  terminalDataSource = new MatTableDataSource<any>([]);
  selection = new SelectionModel<any>(true, []);

  constructor(
    private commandService: CommandService,
    private fb: FormBuilder,
    private authService: AuthenticationService,
    private commandActionAccess: CommandActionAccessService,
    private dialog: MatDialog,
    dialogRef: MatDialogRef<CommandSenderComponent>,
    @Inject(MAT_DIALOG_DATA) data: any
  ) {
    this.dialogRef = dialogRef;
    // Se aberto via dialog, sobrescreve os inputs
    if (data) {
      this.terminalId = data.terminalId;
      this.commandAction = data.commandAction;
      this.streamingSessionUuid = data.streamingSessionUuid;
      this.mode = data.mode;
      if (data.layout) {
        this.layout = data.layout;
      }
      if (Array.isArray(data.terminals)) {
        this.terminals = data.terminals;
      }
      if (Array.isArray(data.selecteds)) {
        this.preselectedTerminals = data.selecteds;
      }

      // Aplica configurações customizadas se fornecidas
      if (data.config) {
        this.applyCustomConfig(data.config);
      }
    }
  }

  /** Indica o layout de abas da opção Comandos (não usado nos atalhos). */
  get isGenericLayout(): boolean {
    return this.layout === 'generic' || this.layout === 'batch';
  }

  /** Indica o modo com lista de terminais e envio em lote. */
  get isBatchLayout(): boolean {
    return this.layout === 'batch';
  }

  /** True quando há ao menos um terminal marcado no modo lote. */
  get hasSelectedTerminals(): boolean {
    return this.getSelectedTerminalIds().length > 0;
  }

  /**
   * Indica se o comando exige reautenticação (campo API `requires_reauth`).
   * Considera o item da lista e o retorno de prepare-form.
   */
  get commandRequiresReauth(): boolean {
    return this.hasRequiresReauth(this.selectedCommand) || this.hasRequiresReauth(this.prepareForm);
  }

  /**
   * Lê `requiresReauth` (camelCase) ou `requires_reauth` (payload original).
   *
   * @param command comando da lista ou do prepare-form
   * @returns true se a API exigir reautenticação
   */
  private hasRequiresReauth(command: CommandModel | null): boolean {
    if (!command) {
      return false;
    }
    const raw = command as CommandModel & { requires_reauth?: boolean; requiresPassword?: boolean };
    return !!(command.requiresReauth || raw.requires_reauth || raw.requiresPassword);
  }

  ngOnInit(): void {
    this.form = this.fb.group({});
    
    // Aplica configurações customizadas se fornecidas via Input
    if (this.config) {
      this.applyCustomConfig(this.config);
    }

    if (this.isBatchLayout) {
      this.initTerminalTable();
      const catalogId = this.getSelectedTerminalIds()[0] || this.terminals[0]?.id_terminal;
      this.terminalId = catalogId ? String(catalogId) : '';
    }
    
    if (!this.terminalId) {
      this.errorMessage = this.isBatchLayout
        ? 'Selecione um terminal.'
        : 'Terminal não informado.';
      return;
    }
    if (this.mode === 'NORMAL') {
      this.fetchAvailableCommands();
    }
  }

  /**
   * Monta a tabela de placas e aplica a pré-seleção do mapa.
   */
  private initTerminalTable(): void {
    const rows = Array.isArray(this.terminals) ? this.terminals : [];
    this.terminalDataSource = new MatTableDataSource<any>(rows);
    this.terminalDataSource.filterPredicate = (row: any, filter: string): boolean => {
      return (row.placa || '').toLowerCase().indexOf(filter) !== -1;
    };
    const preselectedIds = new Set(
      (this.preselectedTerminals.length ? this.preselectedTerminals : rows)
        .map((item) => Number(item.id_terminal))
        .filter((id) => !isNaN(id))
    );
    this.selection.clear();
    rows.forEach((row) => {
      if (preselectedIds.has(Number(row.id_terminal))) {
        this.selection.select(row);
      }
    });
  }

  /**
   * Filtra a lista de placas no modo lote.
   *
   * @param value texto digitado na pesquisa
   */
  handleTerminalFilter(value: string): void {
    this.terminalDataSource.filter = (value || '').trim().toLowerCase();
  }

  /**
   * Indica se todos os terminais visíveis na tabela estão marcados.
   */
  isAllTerminalsSelected(): boolean {
    const rows = this.terminalDataSource.data || [];
    return rows.length > 0 && rows.every((row) => this.selection.isSelected(row));
  }

  /**
   * Marca ou desmarca todos os terminais da lista.
   */
  masterToggleTerminals(): void {
    if (this.isAllTerminalsSelected()) {
      this.selection.clear();
    } else {
      this.terminalDataSource.data.forEach((row) => this.selection.select(row));
    }
    this.onTerminalSelectionChange();
  }

  /**
   * Alterna um terminal e recarrega o catálogo se o veículo de referência sair da seleção.
   *
   * @param row linha da tabela
   */
  toggleTerminal(row: any): void {
    this.selection.toggle(row);
    this.onTerminalSelectionChange();
  }

  /**
   * Recarrega comandos disponíveis quando o terminal de catálogo deixa de estar selecionado.
   */
  onTerminalSelectionChange(): void {
    const ids = this.getSelectedTerminalIds();
    if (!ids.length) {
      return;
    }
    const catalogId = Number(this.terminalId);
    if (ids.indexOf(catalogId) === -1) {
      this.terminalId = String(ids[0]);
      this.selectedCommand = null;
      this.messageFields = [];
      this.form = this.fb.group({});
      this.fetchAvailableCommands();
    }
  }

  /**
   * IDs dos terminais marcados no modo lote.
   */
  getSelectedTerminalIds(): number[] {
    return this.selection.selected
      .map((row) => Number(row && row.id_terminal))
      .filter((id) => !isNaN(id) && id > 0);
  }

  /**
   * Destinos do execute-command: seleção do lote ou o terminal único do popup.
   */
  private getTargetTerminalIds(): number[] {
    if (this.isBatchLayout) {
      return this.getSelectedTerminalIds();
    }
    const id = Number(this.terminalId);
    return isNaN(id) ? [] : [id];
  }

  /**
   * Aplica configurações customizadas ao componente
   * @param config - Configuração customizada
   */
  private applyCustomConfig(config: CommandSenderConfig): void {
    if (config.dialogTitle) {
      this.dialogTitle = config.dialogTitle;
    }
    if (config.submitButtonLabel) {
      this.submitButtonLabel = config.submitButtonLabel;
    }
    if (config.layout) {
      this.layout = config.layout;
    }
  }

  /**
   * Executa um comando diretamente com parâmetros pré-definidos
   * @param commandAction - Ação do comando a ser executado
   * @param parameters - Lista de valores para os campos USER_DEFINED (índice 0 = field_key 0, etc.)
   * @returns Observable com o resultado da execução do comando
   */
  public executeCommandDirectly(commandAction: string, parameters: string[]): Observable<any> {
    if (!this.terminalId) {
      return throwError(() => new Error('Terminal não informado.'));
    }

    this.loading = true;
    this.errorMessage = '';

    return this.commandService.getAvailableCommands(this.terminalId).pipe(
      switchMap(res => {
        const commands = toCamel(res) as CommandModelPaginated;
        const command = commands.content.find(cmd => cmd.commandAction === commandAction);
        
        if (!command) {
          this.loading = false;
          return throwError(() => new Error(`Comando ${commandAction} não encontrado para o terminal ${this.terminalId}`));
        }

        this.selectedCommand = command;
        return this.commandService.getPrepareForm(command);
      }),
      switchMap(res => {
        this.prepareForm = toCamel(res) as CommandModel;
        this.messageFields = (this.prepareForm.messageFields || [])
          .filter((f: MessageFieldModel) => f.commandFieldProcessingType === 'USER_DEFINED')
          .sort((a: MessageFieldModel, b: MessageFieldModel) => a.displayOrder - b.displayOrder);

        // Valida se o número de parâmetros corresponde ao número de campos USER_DEFINED
        if (parameters.length !== this.messageFields.length) {
          this.loading = false;
          return throwError(() => new Error(
            `Número de parâmetros (${parameters.length}) não corresponde ao número de campos USER_DEFINED (${this.messageFields.length})`
          ));
        }

        // Faz o bind dos parâmetros com os campos USER_DEFINED
        const fieldValues: { [uuid: string]: string } = {};
        this.messageFields.forEach((field, index) => {
          fieldValues[field.uuid] = parameters[index];
        });

        // Cria o payload para execução do comando
        const payload = new CommandExecutionRequest(
          fieldValues,
          [Number(this.terminalId)],
          this.authService.currentUserValue?.id
        );

        // Executa o comando
        return this.commandService.executeCommand(this.selectedCommand, toSnake(payload));
      }),
      tap(() => {
        this.loading = false;
        console.log(`Comando ${commandAction} executado com sucesso para o terminal ${this.terminalId}`);
      }),
      catchError(error => {
        this.loading = false;
        this.errorMessage = `Erro ao executar comando: ${error.message}`;
        console.error('Erro na execução direta do comando:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Busca os comandos disponíveis no terminal.
   * Layout generic/batch: intersecta com `comandosEfetivos` do perfil (`usuarios/me`).
   * Atalhos: restringe apenas pela `commandAction` fixa.
   */
  fetchAvailableCommands() {
    this.loading = true;
    this.commandService.getAvailableCommands(this.terminalId).subscribe({
      next: (res) => {
        this.availableCommands = toCamel(res) as CommandModelPaginated;
        if (!this.availableCommands.content) {
          this.availableCommands.content = [];
        }
        if (this.commandAction) {
          this.availableCommands.content = this.availableCommands.content.filter(
            cmd => cmd.commandAction === this.commandAction
          );
        } else if (this.isGenericLayout) {
          this.availableCommands.content = this.commandActionAccess.filterByAccess(
            this.availableCommands.content
          );
        }
        if (this.availableCommands.content.length === 1) {
          this.selectedCommand = this.availableCommands.content[0];
          this.fetchPrepareForm(this.selectedCommand);
          return;
        }
        if (this.availableCommands.content.length === 0) {
          this.errorMessage = this.isGenericLayout
            ? 'Nenhum comando disponível para o seu perfil.'
            : 'Nenhum comando disponível.';
        }
        this.loading = false;
      },
      error: (err) => {
        this.errorMessage = 'Erro ao buscar comandos disponíveis.';
        this.loading = false;
      }
    });
  }

  fetchPrepareForm(command: CommandModel) {
    this.loading = true;
    this.commandService.getPrepareForm(command).subscribe({
      next: (res) => {
        this.prepareForm = toCamel(res) as CommandModel;
        const raw = this.prepareForm as CommandModel & { char_delimiter?: string };
        this.charDelimiter = this.prepareForm.charDelimiter || raw.char_delimiter || ',';
        this.messageFields = (this.prepareForm.messageFields || []).filter((f: MessageFieldModel) => f.commandFieldProcessingType === 'USER_DEFINED')
          .sort((a: MessageFieldModel, b: MessageFieldModel) => a.displayOrder - b.displayOrder);
        
        // Processa as opções dos campos SELECT
        this.messageFields.forEach(field => {
          if (field.fieldInputType === 'SELECT' && field.fieldOptions) {
            try {
              const options = JSON.parse(field.fieldOptions) as SelectOptions;
              this.fieldOptions.set(field.uuid, options.options);
            } catch (error) {
              console.error(`Erro ao processar opções do campo ${field.fieldLabel}:`, error);
            }
          }
        });

        this.buildForm();
        this.loading = false;
      },
      error: (err) => {
        this.errorMessage = 'Erro ao buscar dados do comando.';
        this.loading = false;
      }
    });
  }

  // Adiciona método para criar validador de diferença de tempo
  private createTimeDifferenceValidator(field1: string, field2: string, maxMinutes: number): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const control1 = formGroup.get(field1);
      const control2 = formGroup.get(field2);

      if (!control1 || !control2) {
        return null;
      }

      const date1 = new Date(control1.value);
      const date2 = new Date(control2.value);

      if (isNaN(date1.getTime()) || isNaN(date2.getTime())) {
        return null;
      }

      const diffMinutes = Math.abs((date2.getTime() - date1.getTime()) / (1000 * 60));
      
      return diffMinutes > maxMinutes ? { timeDifference: true } : null;
    };
  }

  // Adiciona método para criar validador de dependência entre campos
  private createDependentFieldValidator(field1: string, field2: string, condition: (value1: any, value2: any) => boolean): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const control1 = formGroup.get(field1);
      const control2 = formGroup.get(field2);

      if (!control1 || !control2) {
        return null;
      }

      return condition(control1.value, control2.value) ? null : { dependentFields: true };
    };
  }

  buildForm() {
    const group: any = {};
    const crossFieldValidators: ValidatorFn[] = [];

    this.messageFields.forEach(field => {
      const defaultValue = field.defaultValue != null ? field.defaultValue : '';
      if (this.isMultiValueField(field)) {
        group[field.fieldKey] = this.fb.array([
          new FormControl(defaultValue, this.buildFieldValidators(field, true))
        ]);
      } else {
        group[field.fieldKey] = new FormControl(defaultValue, this.buildFieldValidators(field, true));
      }
    });

    // Adiciona validações cruzadas baseadas no tipo de campo
    this.messageFields.forEach(field => {
      // Exemplo: Validação de diferença de tempo para campos de data
      if (field.fieldInputType === 'DATETIME' && !this.isMultiValueField(field)) {
        const nextField = this.messageFields.find(f => 
          f.fieldInputType === 'DATETIME' && 
          f.displayOrder > field.displayOrder &&
          !this.isMultiValueField(f)
        );
        
        if (nextField) {
          crossFieldValidators.push(
            this.createTimeDifferenceValidator(field.fieldKey, nextField.fieldKey, 5)
          );
        }
      }
    });

    this.form = this.fb.group(group, { validators: crossFieldValidators });
  }

  /**
   * Monta validadores do campo (regex / required).
   *
   * @param field metadado do prepare-form
   * @param requiredItem se o item exige valor (primeiro item em multi-valor)
   */
  private buildFieldValidators(field: MessageFieldModel, requiredItem: boolean): any[] {
    const validators: any[] = [];
    if (field.validationRegex) {
      validators.push(Validators.pattern(field.validationRegex));
    }
    if (requiredItem && (field.fieldInputType === 'SELECT' || field.required)) {
      validators.push(Validators.required);
    }
    return validators;
  }

  /**
   * Indica se o campo permite lista de valores (`allows_multiple_values`).
   *
   * @param field metadado do prepare-form
   */
  isMultiValueField(field: MessageFieldModel): boolean {
    if (!field) {
      return false;
    }
    const raw = field as MessageFieldModel & { allows_multiple_values?: boolean };
    return !!(field.allowsMultipleValues || raw.allows_multiple_values);
  }

  /**
   * Limite de itens do campo multi-valor (`max_values`).
   *
   * @param field metadado do prepare-form
   */
  getMaxValues(field: MessageFieldModel): number | null {
    if (!field) {
      return null;
    }
    const raw = field as MessageFieldModel & { max_values?: number };
    const max = field.maxValues != null ? field.maxValues : raw.max_values;
    return max != null && max > 0 ? max : null;
  }

  /**
   * FormArray do campo multi-valor.
   *
   * @param field metadado do prepare-form
   */
  getFieldArray(field: MessageFieldModel): FormArray {
    return this.form.get(field.fieldKey) as FormArray;
  }

  /**
   * Adiciona um valor vazio ao FormArray (respeitando `maxValues`).
   *
   * @param field metadado do prepare-form
   */
  addMultiValue(field: MessageFieldModel): void {
    const array = this.getFieldArray(field);
    if (!array) {
      return;
    }
    const max = this.getMaxValues(field);
    if (max != null && array.length >= max) {
      return;
    }
    array.push(new FormControl('', this.buildFieldValidators(field, false)));
  }

  /**
   * Remove um valor do FormArray (mínimo 1 item).
   *
   * @param field metadado do prepare-form
   * @param index índice do item
   */
  removeMultiValue(field: MessageFieldModel, index: number): void {
    const array = this.getFieldArray(field);
    if (!array || array.length <= 1) {
      return;
    }
    array.removeAt(index);
  }

  /**
   * Indica se ainda é possível adicionar valores ao campo.
   *
   * @param field metadado do prepare-form
   */
  canAddMultiValue(field: MessageFieldModel): boolean {
    const array = this.getFieldArray(field);
    if (!array) {
      return false;
    }
    const max = this.getMaxValues(field);
    return max == null || array.length < max;
  }

  /**
   * Resolve o valor enviado ao backend (join com `charDelimiter` se multi-valor).
   *
   * @param field metadado do prepare-form
   */
  private resolveFieldSubmitValue(field: MessageFieldModel): string {
    const control = this.form.get(field.fieldKey);
    if (!control) {
      return '';
    }
    if (this.isMultiValueField(field) && control instanceof FormArray) {
      const delimiter = this.charDelimiter || ',';
      return control.controls
        .map((entry) => String(entry.value != null ? entry.value : '').trim())
        .filter((value) => !!value)
        .join(delimiter);
    }
    const value = control.value;
    return value != null ? String(value) : '';
  }

  // Adiciona método para obter mensagens de erro de validação cruzada
  getCrossFieldErrorMessage(): string {
    if (this.form.hasError('timeDifference')) {
      return 'A diferença entre as datas deve ser menor que 5 minutos';
    }
    if (this.form.hasError('dependentFields')) {
      return 'Os campos selecionados são incompatíveis';
    }
    return '';
  }

  /**
   * Seleciona um comando da lista e carrega o formulário dinâmico.
   *
   * @param uuid identificador do comando escolhido
   */
  onCommandSelect(uuid: string) {
    this.errorMessage = '';
    this.successMessage = '';
    this.messageFields = [];
    this.selectedCommand = this.availableCommands.content.find(cmd => cmd.uuid === uuid) as CommandModel;
    if (this.selectedCommand) {
      this.fetchPrepareForm(this.selectedCommand);
    }
  }

  /**
   * Envio clássico (atalhos): valida e dispara o comando, fechando o dialog em sucesso.
   */
  submit() {
    this.sendCommand(true);
  }

  /**
   * Confirma o envio no layout genérico, pedindo senha quando o comando exigir.
   */
  confirmGeneric(): void {
    if (!this.selectedCommand) {
      return;
    }
    if (this.isBatchLayout && !this.hasSelectedTerminals) {
      this.errorMessage = 'Selecione um terminal';
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (this.commandRequiresReauth) {
      this.openStepUpDialog();
      return;
    }
    this.sendCommand(false);
  }

  /**
   * Abre o dialog de senha (POST /auth/step-up). Em sucesso, dispara o envio.
   *
   * @param error mensagem de sessão expirada quando o execute-command retorna 403
   */
  private openStepUpDialog(error?: string): void {
    const authRef = this.dialog.open(CommandAuthDialogComponent, {
      width: '420px',
      data: {
        commandName: this.selectedCommand
          ? this.selectedCommand.name || this.selectedCommand.description
          : '',
        error,
        message: 'Este comando exige reautenticação. Digite sua senha para continuar.'
      }
    });
    authRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.sendCommand(false);
      }
    });
  }

  /**
   * Monta o payload e executa o comando no hardware.
   *
   * @param closeOnSuccess fecha o dialog após sucesso (atalhos); no layout genérico permanece aberto
   */
  private sendCommand(closeOnSuccess: boolean): void {
    if (!this.selectedCommand) {
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';
    const fieldValues: { [uuid: string]: string } = {};
    this.messageFields.forEach(field => {
      fieldValues[field.uuid] = this.resolveFieldSubmitValue(field);
    });
    const terminalIds = this.getTargetTerminalIds();
    if (!terminalIds.length) {
      this.loading = false;
      this.errorMessage = 'Selecione um terminal';
      return;
    }
    const payload = new CommandExecutionRequest(
      fieldValues,
      terminalIds,
      this.authService.currentUserValue?.id
    );
    this.commandService.executeCommand(this.selectedCommand, toSnake(payload)).subscribe({
      next: (res) => {
        this.loading = false;
        if (closeOnSuccess) {
          this.dialogRef.close({ ...res, streamingSessionUuid: this.streamingSessionUuid });
          return;
        }
        this.successMessage = terminalIds.length > 1
          ? 'Comando enviado para ' + terminalIds.length + ' terminais.'
          : 'Comando enviado com sucesso.';
        this.loadSentCommands(this.historyPageIndex, this.historyPageSize);
      },
      error: (err) => {
        this.loading = false;
        if (this.isGenericLayout && isInsufficientAuthError(err)) {
          this.openStepUpDialog(
            'Sua sessão expirou. Digite sua senha novamente para continuar.'
          );
          return;
        }
        this.errorMessage = 'Erro ao enviar comando.';
      }
    });
  }

  /**
   * Carrega o histórico ao abrir a aba "Comandos enviados".
   *
   * @param index índice da aba selecionada
   */
  onGenericTabChange(index: number): void {
    if (index === 1) {
      this.loadSentCommands(0, this.historyPageSize);
    }
  }

  /**
   * Busca o histórico em command-executions (mesma API do setera-admin-web).
   *
   * @param pageIndex índice da página (0-based)
   * @param pageSize quantidade de itens por página
   */
  loadSentCommands(pageIndex = this.historyPageIndex, pageSize = this.historyPageSize): void {
    const terminalIdsParam = this.isBatchLayout
      ? this.getSelectedTerminalIds().join(',')
      : this.terminalId;
    if (!terminalIdsParam) {
      this.sentCommandItems = [];
      this.historyTotalElements = 0;
      this.currentHistoryPageResponse = null;
      this.historyErrorMessage = this.isBatchLayout
        ? 'Selecione um terminal para ver o histórico.'
        : 'Terminal não informado.';
      this.historyLoading = false;
      return;
    }
    this.historyPageIndex = pageIndex;
    this.historyPageSize = pageSize;
    this.historyLoading = true;
    this.historyErrorMessage = '';
    this.historySuccessMessage = '';
    this.currentHistoryPageResponse = null;
    this.commandService.getCommandExecutions(terminalIdsParam, pageIndex, pageSize).subscribe({
      next: (res) => {
        this.applyHistoryPage(res);
        this.historyLoading = false;
      },
      error: () => {
        this.historyErrorMessage = 'Não foi possível carregar os comandos enviados.';
        this.sentCommandItems = [];
        this.historyTotalElements = 0;
        this.currentHistoryPageResponse = null;
        this.historyLoading = false;
      }
    });
  }

  /**
   * Atualiza estado da tabela e paginação a partir da resposta HAL.
   *
   * @param payload resposta bruta da API
   */
  private applyHistoryPage(payload: any): void {
    const camel = toCamel(payload) || {};
    this.sentCommandItems = this.extractExecutionContent(camel).map(
      (item) => toCamel(item) as CommandExecutionHistoryItem
    );
    // Metadados de página vêm da API já em camelCase; preferir payload bruto.
    const pageInfo = extractExecutionPageInfo(
      payload && payload.page != null ? payload : camel
    );
    this.historyPageSize = pageInfo.size;
    this.historyTotalElements = pageInfo.totalElements;
    this.historyPageIndex = pageInfo.number;
    this.currentHistoryPageResponse = payload;
  }

  /**
   * Navega entre páginas via page/size (paridade funcional com admin-web).
   * Evita seguir links HAL ambíguos do HateoasBaseService, que podiam
   * reutilizar o `self` (page=0) no lugar de `next`/`prev`.
   *
   * @param event evento do mat-paginator
   */
  onHistoryPageChange(event: PageEvent): void {
    const nextPage = event.pageIndex;
    const nextSize = event.pageSize;
    if (nextPage === this.historyPageIndex && nextSize === this.historyPageSize) {
      return;
    }
    this.loadSentCommands(nextPage, nextSize);
  }

  /**
   * Texto exibido na coluna Comando (tooltip: payload enviado).
   */
  getCommandDisplay(item: CommandExecutionHistoryItem): string {
    return item.commandName || 'Comando';
  }

  /**
   * Tooltip da coluna Comando.
   */
  getCommandTooltip(item: CommandExecutionHistoryItem): string {
    return item.commandSent || '-';
  }

  /**
   * Texto exibido na coluna Resposta.
   */
  getResponseDisplay(item: CommandExecutionHistoryItem): string {
    return item.result || '-';
  }

  /**
   * Tooltip da coluna Resposta.
   */
  getResponseTooltip(item: CommandExecutionHistoryItem): string {
    return item.response || item.errorMessage || '-';
  }

  /**
   * Confirma e dispara o reenvio de uma execução.
   */
  confirmResend(item: CommandExecutionHistoryItem): void {
    const dialogRef = this.dialog.open(CommandHistoryConfirmDialogComponent, {
      width: '420px',
      data: {
        title: 'Reenviar comando',
        message: `Deseja reenviar o comando "${this.getCommandDisplay(item)}" para o terminal ${item.terminalId != null ? item.terminalId : '-'}?`,
        confirmLabel: 'Sim, reenviar',
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.runExecutionAction(item, 'resend');
      }
    });
  }

  /**
   * Confirma e dispara o cancelamento de uma execução.
   */
  confirmCancel(item: CommandExecutionHistoryItem): void {
    const dialogRef = this.dialog.open(CommandHistoryConfirmDialogComponent, {
      width: '420px',
      data: {
        title: 'Cancelar comando',
        message: `Deseja cancelar o comando "${this.getCommandDisplay(item)}" para o terminal ${item.terminalId != null ? item.terminalId : '-'}?`,
        confirmLabel: 'Sim, cancelar',
        warn: true,
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.runExecutionAction(item, 'cancel');
      }
    });
  }

  /**
   * Executa reenvio/cancelamento com step-up quando `requiresReauth`.
   */
  private runExecutionAction(item: CommandExecutionHistoryItem, action: 'resend' | 'cancel'): void {
    const execute = () => this.performExecutionAction(item, action);
    if (executionRequiresReauth(item)) {
      this.openHistoryStepUpDialog(execute);
      return;
    }
    execute();
  }

  /**
   * Chama HAL resend/cancel e atualiza a listagem.
   */
  private performExecutionAction(item: CommandExecutionHistoryItem, action: 'resend' | 'cancel'): void {
    this.historyActionUuid = item.uuid || null;
    this.historyErrorMessage = '';
    this.historySuccessMessage = '';
    const request$ = action === 'resend'
      ? this.commandService.resendCommandExecution(item)
      : this.commandService.cancelCommandExecution(item);

    request$.subscribe({
      next: () => {
        this.historyActionUuid = null;
        this.historySuccessMessage = action === 'resend'
          ? 'Comando reenviado com sucesso.'
          : 'Comando cancelado com sucesso.';
        this.loadSentCommands(this.historyPageIndex, this.historyPageSize);
      },
      error: (err) => {
        this.historyActionUuid = null;
        if (isInsufficientAuthError(err)) {
          this.openHistoryStepUpDialog(
            () => this.performExecutionAction(item, action),
            'Sua sessão expirou. Digite sua senha novamente para continuar.'
          );
          return;
        }
        this.historyErrorMessage = action === 'resend'
          ? 'Erro ao reenviar o comando.'
          : 'Erro ao cancelar o comando.';
      }
    });
  }

  /**
   * Dialog de senha para reenviar/cancelar quando a API exige reautenticação.
   */
  private openHistoryStepUpDialog(onConfirmed: () => void, error?: string): void {
    const authRef = this.dialog.open(CommandAuthDialogComponent, {
      width: '420px',
      data: {
        commandName: '',
        error,
        message: 'Esta ação exige reautenticação. Digite sua senha para continuar.'
      }
    });
    authRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        onConfirmed();
      }
    });
  }

  /**
   * Indica se há ação em andamento para desabilitar botões da linha.
   */
  isHistoryActionLoading(item: CommandExecutionHistoryItem): boolean {
    return !!this.historyActionUuid && this.historyActionUuid === item.uuid;
  }

  /**
   * Extrai a lista de execuções de respostas HAL/Spring page.
   *
   * @param payload resposta já convertida para camelCase
   * @returns itens do histórico
   */
  private extractExecutionContent(payload: any): CommandExecutionHistoryItem[] {
    if (!payload) {
      return [];
    }
    if (Array.isArray(payload)) {
      return payload;
    }
    if (Array.isArray(payload.content)) {
      return payload.content;
    }
    const embedded = payload.embedded || payload._embedded;
    if (embedded && typeof embedded === 'object') {
      const first = Object.keys(embedded).map((key) => embedded[key])[0];
      if (Array.isArray(first)) {
        return first;
      }
    }
    return [];
  }

  /**
   * Formata data para exibição na tabela de enviados.
   *
   * @param value timestamp, Date ou string
   * @returns data formatada
   */
  formatDisplayDate(value: Date | string | number): string {
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) {
      return '';
    }
    const pad = (n: number) => (n < 10 ? '0' + n : String(n));
    return pad(date.getDate()) + '/' + pad(date.getMonth() + 1) + '/' + date.getFullYear()
      + ' ' + pad(date.getHours()) + ':' + pad(date.getMinutes());
  }

  close() {
    this.dialogRef.close(/**{ streamingSessionUuid: this.streamingSessionUuid }**/);
  }
} 